import { useEffect } from 'react';
import audioButton from '@/assets/marketing/audio-button-transparent.svg';

export type StorybookPageData = {
  image: string | null;
  sound: string | null;
  soundChildren: string | null;
};

function isTypingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export function storybookPageAudio(
  page: StorybookPageData | undefined,
  voice: 'adult' | 'child',
): string | null {
  if (!page) return null;
  return voice === 'adult' ? page.sound : page.soundChildren || page.sound;
}

type StorybookReaderProps = {
  pages: StorybookPageData[];
  index: number;
  onIndexChange: (index: number) => void;
  onPlayAudio: () => void;
  onExit: () => void;
};

export function StorybookReader({
  pages,
  index,
  onIndexChange,
  onPlayAudio,
  onExit,
}: StorybookReaderProps) {
  const lastIndex = pages.length - 1;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (isTypingTarget(event.target)) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (index < lastIndex) onIndexChange(index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (index > 0) onIndexChange(index - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, lastIndex, onIndexChange]);

  const page = pages[index];
  if (!page) return null;

  return (
    <div
      data-testid="storybook-reader"
      className="relative flex min-h-[calc(100vh-6rem)] flex-col bg-black px-4 py-5 text-white lg:min-h-screen"
    >
      <header className="mx-auto flex w-full max-w-5xl items-center justify-between">
        <button type="button" onClick={onExit} className="font-bold text-white/80">
          ← Exit story
        </button>
        <p className="text-sm text-slate-300" aria-live="polite">
          {index + 1} / {pages.length}
        </p>
      </header>

      <div className="mx-auto mt-5 flex min-h-0 w-full max-w-5xl flex-1 flex-col items-center justify-center">
        {page.image ? (
          <img
            src={page.image}
            alt={`Page ${index + 1}`}
            className="max-h-[64vh] max-w-full rounded-2xl object-contain shadow-2xl"
          />
        ) : (
          <div className="rounded-2xl bg-white/10 p-10">Page {index + 1}</div>
        )}
        {/* Mobile parity: sit in the letterbox below the page so the speaker
            does not cover the story text printed on the image. */}
        <button
          type="button"
          data-testid="storybook-speaker"
          onClick={onPlayAudio}
          aria-label="Play page audio"
          className="mt-4 h-11 w-11 shrink-0"
        >
          <img src={audioButton} alt="" className="h-full w-full" />
        </button>
      </div>

      <div className="mx-auto mt-5 flex w-full max-w-xl justify-center gap-3">
        <button
          type="button"
          data-testid="storybook-prev"
          onClick={() => onIndexChange(index - 1)}
          disabled={index === 0}
          className="rounded-2xl border border-white/20 px-5 py-3 font-bold disabled:opacity-30"
        >
          Previous
        </button>
        <button
          type="button"
          data-testid="storybook-next"
          onClick={() => onIndexChange(index + 1)}
          disabled={index === pages.length - 1}
          className="rounded-2xl border border-white/20 px-5 py-3 font-bold disabled:opacity-30"
        >
          Next
        </button>
      </div>
    </div>
  );
}
